import React, { useState, useEffect } from "react";
import HTMLFlipBook from "react-pageflip";

export default function About() {
  const images = [
    `${process.env.PUBLIC_URL}/Slide.avif`,
    `${process.env.PUBLIC_URL}/Slide2.avif`,
    `${process.env.PUBLIC_URL}/Slide3.avif`,
    `${process.env.PUBLIC_URL}/Slide4.avif`,
    `${process.env.PUBLIC_URL}/Slide5.avif`,
  ];

  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 2750);
    return () => clearInterval(interval);
  }, [images.length]);

  // ✅ Track window size for responsive flipbook
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowSize.width < 768;

  const pages = [
    {
      title: "Small Book About Me",
      image: `${process.env.PUBLIC_URL}/Book1.jpg`,
      content: (
        <p className="text-gray-300 mb-6 leading-relaxed text-sm md:text-base">
          Hi, I’m Mithil Peeris — a passionate Computer Science student at Toronto Metropolitan University 
          with a strong focus on AI, software development, and data analytics. With a CGPA of 3.85 and consistent 
          placement on the Dean’s List, I combine academic excellence with hands-on project experience to build 
          innovative solutions that create real-world impact.
        </p>
      ),
    },
    {
      title: "Experience",
      image: `${process.env.PUBLIC_URL}/Book2.png`,
      content: (
        <p className="text-gray-300 mb-6 leading-relaxed text-sm md:text-base">
          I’ve worked on everything from building a facial recognition system with 98.76% validation accuracy to developing full-stack applications and solving complex algorithmic problems in real-world settings. 
          My experience spans software development, machine learning, databases, and cloud systems across a wide range of languages and tools. 
          I’m always eager to learn fast, adapt quickly, and turn ambitious ideas into real results.
        </p>
      ),
    },
    {
      title: "Leadership",
      image: `${process.env.PUBLIC_URL}/Book3.jpg`,
      content: (
        <p className="text-gray-300 leading-relaxed text-sm md:text-base">
          Beyond the technical world, I’m a people person who loves building strong teams and organizing events — whether through my leadership 
          roles as Vice President of Events for TMU SLSA and Social Chair at Alpha Epsilon Pi, or through competing in basketball leagues across Ontario. 
          Those experiences have strengthened my teamwork, leadership, and ability to connect with people, and I bring that same collaborative, creative mindset to every project I take on.
        </p>
      ),
    },
    {
      title: "ChatGPT’s Take",
      image: `${process.env.PUBLIC_URL}/Book4.jpg`,
      content: (
        <>
          <p className="text-gray-300 leading-relaxed italic text-sm md:text-base">
            Thought I’d share ChatGPT’s version of me — and honestly, it’s spot on.
          
            An analytical powerhouse with a creative edge — part coder, part connector, and full-on problem solver. 
            More than a generalist, you’re someone who performs at a high level across disciplines, bringing technical depth, leadership, and real follow-through to everything you do. 
            Whether building software, leading teams, or tackling complex ideas, you turn thought into action and ideas into results.
          </p>
        </>
      ),
    },
  ];

  const scrollToAbout = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <>
      {/* Hero Section with Slideshow */}
      <div
        className="relative text-white overflow-hidden min-h-screen flex flex-col items-center justify-center transition-all duration-1000"
        style={{
          backgroundImage: `url(${images[currentImage]})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <h1 className="text-3xl md:text-5xl font-bold mb-8 text-purple-400">Welcome to My Story</h1>
        <button 
          onClick={scrollToAbout}
          className="bg-purple-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-full hover:bg-purple-700 transition text-lg"
        >
          View About Page
        </button>
      </div>

      {/* Book Flip Section with Sticky Footer */}
      <div 
        className="flex flex-col justify-between min-h-screen bg-gray-800 text-white px-4 md:px-8 pt-20 z-50"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/BG3.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex-1 flex items-center justify-center w-full mb-20">
          <HTMLFlipBook
            width={isMobile ? Math.min(windowSize.width * 0.9, 380) : 700}
            height={isMobile ? 700 : 800}
            className="shadow-lg rounded-lg mx-auto"
            mobileScrollSupport={true}
          >
            {pages.map((page, index) => (
              <div
                key={index}
                className="bg-black/60 backdrop-blur-md p-2 md:p-8 rounded-lg h-full relative flex items-center justify-center"
              >
                <div className="w-full h-full flex flex-col items-center justify-center text-center">
                  <h1 className="text-2xl md:text-3xl font-bold text-purple-400 mb-10">
                    {page.title}
                  </h1>

                  {page.image && (
                    <img
                      src={page.image}
                      alt={page.title}
                      loading="lazy"
                      decoding="async"
                      className="w-60 h-60 md:w-80 md:h-80 object-cover rounded-2xl mb-10"
                    />
                  )}

                  <div className="max-w-[90%] flex items-center justify-center">
                    {page.content}
                  </div>
                </div>

                {(isMobile || index === 1 || index === 2) && (
                  <div className="absolute bottom-4 right-4 text-gray-400 text-xs animate-bounce">
                    Click to Flip →
                  </div>
                )}
              </div>
            ))}
          </HTMLFlipBook>
        </div>
      </div>
    </>
  );
}